import DotCorrect from "./Images/dot-correct.svg";
import DotPartial from "./Images/dot-partial.svg";
import DotIncorrect from "./Images/dot-incorrect.svg";
import RailDotCorrect from "./Images/rail-dot-correct.svg";
import RailDotPartial from "./Images/rail-dot-partial.svg";
import RailDotIncorrect from "./Images/rail-dot-incorrect.svg";

/**
 * The report is a printed A4 document, not an app screen. It deliberately does
 * not follow the classroom light/dark tokens: it keeps the document's own
 * cream-and-white palette whichever theme the app is in, because it is meant to
 * be printed and handed over. Values are the literal hexes from Figma 95:38 and
 * 96:128.
 */
export const r = {
  page: "#fbfbfb",
  card: "#ffffff",
  ink: "#1e1e1e",
  inkSoft: "#2f2f30",
  muted: "#6b6a6e",
  chip: "#3d4041",
  onChip: "#ffffff",
  gradeBg: "#f9f7f1",
  gradeBorder: "#3d4041",
  gradeText: "#ff3930",
  pillBg: "#f9f7f1",
  // The outlined pills (status, question topic) and the Model Answer card, all
  // on the page tint with a 0.25pt hairline (Figma 96:208).
  outlineBg: "#fbfbfb",
  // The design asks for a 0.25pt hairline on these pills. No browser will draw
  // one: Chrome computes any non-zero border under 1px up to a full 1px, so
  // both "0.25px" and the 4/3-scaled "0.333px" came out as a heavy 1px line.
  // A 1px border carrying roughly a quarter of the ink is the same thing to
  // the eye and is what actually renders, on screen and on paper.
  outlineBorder: "rgba(0,0,0,0.38)",
  hairline: "1px",
  modelBg: "#fbfbfb",
  // The added parts of a model answer (Figma 103:36): a flat highlighter
  // block, square corners.
  highlight: "#f1f455",
  // Editing chrome. Only ever on screen, never in print.
  editBorder: "#d8d4cc",
  editFocus: "#ff4716",
  sepCyan: "#02c9fe",
  sepInk: "#1e1e1e",
  rule: "#000000",
};

/**
 * The three correctness bands. `bg` is the tint behind the Feedback block,
 * `dot` the 13px marker beside it and on the floating status pill, `rail` the
 * 6px dot that caps the timeline down the left of the card.
 *
 * The backend stores "PARTIALLY CORRECT" with a space (FeedbackService).
 */
export const TONES = {
  CORRECT: { label: "Correct", bg: "#f8fff3", dot: DotCorrect, rail: RailDotCorrect },
  // Partial moved off the page tint to amber once the Model Answer card took
  // that tint over, so the two blocks never read as one.
  PARTIAL: { label: "Partially Correct", bg: "#fff5e4", dot: DotPartial, rail: RailDotPartial },
  INCORRECT: { label: "Incorrect", bg: "#ffedec", dot: DotIncorrect, rail: RailDotIncorrect },
};

export const toneFor = (correctness, scoredMark, totalMark) => {
  const normalised = (correctness || "").toUpperCase().replace(/[\s_]+/g, "");
  if (normalised === "CORRECT") return TONES.CORRECT;
  if (normalised === "PARTIALLYCORRECT") return TONES.PARTIAL;
  if (normalised === "INCORRECT") return TONES.INCORRECT;
  // Fall back to the marks if correctness was never written.
  if (totalMark && scoredMark >= totalMark) return TONES.CORRECT;
  return scoredMark > 0 ? TONES.PARTIAL : TONES.INCORRECT;
};

/**
 * The Figma frames are drawn on A4 at 72dpi (596pt wide). The sheet renders at
 * A4 at 96dpi (794px), so every measurement taken from the design is scaled by
 * 4/3. Wrapping them in s() keeps the raw Figma number visible at the call site,
 * so the code and the design stay easy to compare.
 */
export const s = (n) => `${(n * 4) / 3}px`;

export const SHEET_WIDTH = 794;   // A4 width  at 96dpi
export const SHEET_HEIGHT = 1123; // A4 height at 96dpi
